//dashFrameSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const authHeader = (access_token) =>
  access_token ? { headers: { Authorization: `Bearer ${access_token}` } } : {};

export const fetchTableOne = createAsyncThunk(
  "dashFrame/fetchTableOne",
  async (
    { liverun, license_key, access_token },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_count_by_week`,
        { liverun, license_key },
        authHeader(access_token),
      );

      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchTableTwo = createAsyncThunk(
  "dashFrame/fetchTableTwo",
  async (
    { start_date, end_date, liverun, license_key, access_token },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_category_by_week`,
        { start_date, end_date, liverun, license_key },
        authHeader(access_token),
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchTableThree = createAsyncThunk(
  "dashFrame/fetchTableThree",
  async (
    { start_date, end_date, liverun, license_key, access_token },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_week_by_date`,
        { start_date, end_date, liverun, license_key },
        authHeader(access_token),
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchDayByCategory = createAsyncThunk(
  "dashFrame/fetchDayByCategory",
  async (
    { date, liverun, license_key, access_token },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_category_by_date`,
        { date, liverun, license_key },
        authHeader(access_token),
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchDayData = createAsyncThunk(
  "dashFrame/fetchDayData",
  async (
    { date, liverun, license_key, access_token },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_data_by_date`,
        { date, liverun, license_key },
        authHeader(access_token),
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

const handleError = (err, rejectWithValue) => {
  if (err.response && err.response.data) {
    return rejectWithValue(err.response.data.status || err.response.data);
  }
  return rejectWithValue(err.message || "Something went wrong");
};

export const fetchDateRangeExcel = createAsyncThunk(
  "dashFrame/fetchDateRangeExcel",
  async (
    { start_date, end_date, liverun, license_key, access_token },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_data_by_date_range`,
        { start_date, end_date, liverun, license_key },
        {
          ...authHeader(access_token),
          responseType: "blob",
        },
      );

      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match
        ? match[1]
        : `blood_filings_${start_date}_to_${end_date}.xlsx`;

      return { blob: res.data, filename };
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      // NOTE: with responseType: "blob", a JSON error body from the backend
      // (e.g. HttpResponseBadRequest) also arrives as a Blob, not parsed JSON.
      // If you want the actual error text/reason surfaced to the user, read it here:
      //   const text = await error.response?.data?.text?.();
      return handleError(error, rejectWithValue);
    }
  },
);

export const fetchFiling = createAsyncThunk(
  "dashFrame/fetchFiling",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/fetch_blood_filing`,
        { lab_report_id, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

const initialState = {
  liverun: true,

  tableOne: [],
  tableOneTotalProcessed: 0,
  tableOneTotalNormal: 0,
  tableOneTotalAbnormal: 0,
  tableOneInreviewCategories: 0,
  tableOneEnabledCategories: 0,
  tableOneMonthlyTrend: [],

  tableTwo: [],
  tableThree: [],
  tableDayByCategory: [],
  tableDayData: [],
  lab: {},
  selectedDate: null,
  selectedDay: null,
  selectedLabReport: null,
  loadingOne: false,
  loadingTwo: false,
  loadingThree: false,
  loadingDayByCategory: false,
  loadingDayData: false,
  loadingLabReport: false,
};

const dashFrameSlice = createSlice({
  name: "dashFrame",
  initialState,
  reducers: {
    toggleLiverun(state) {
      state.liverun = !state.liverun;
      state.tableTwo = [];
      state.tableThree = [];
      state.selectedDate = null;
      state.tableDayByCategory = [];
      state.tableDayData = [];
      state.selectedDay = null;
      state.lab = {};
      state.selectedLabReport = null;
    },
    resetDashboard: () => initialState,
    setSelectedDate(state, action) {
      state.selectedDate = action.payload;
    },
    setSelectedDay(state, action) {
      state.selectedDay = action.payload;
    },
    setLabReport(state, action) {
      state.selectedLabReport = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Table One
      .addCase(fetchTableOne.pending, (state) => {
        state.loadingOne = true;
      })
      .addCase(fetchTableOne.fulfilled, (state, action) => {
        const {
          count_by_week = [],
          total_processed = 0,
          total_normal = 0,
          total_abnormal = 0,
          inreview_categories = 0,
          enabled_categories = 0,
          result = [],
        } = action.payload;

        state.tableOne = count_by_week.map((rec, idx) => ({
          id: idx,
          date: rec[0],
          start_date: rec[1],
          end_date: rec[2],
          filed: rec[4],
          reviewed_and_actioned: rec[5],
          not_filed_inconclusive: rec[6],
          not_filed_setup: rec[7],
          not_filed_already: rec[8],
          error: rec[9],
        }));

        state.tableOneTotalProcessed = total_processed;
        state.tableOneTotalNormal = total_normal;
        state.tableOneTotalAbnormal = total_abnormal;
        state.tableOneInreviewCategories = inreview_categories;
        state.tableOneEnabledCategories = enabled_categories;
        state.tableOneMonthlyTrend = result;

        state.loadingOne = false;
      })

      // Table Two
      .addCase(fetchTableTwo.pending, (state) => {
        state.loadingTwo = true;
      })
      .addCase(fetchTableTwo.fulfilled, (state, action) => {
        state.tableTwo = action.payload.map((rec, idx) => ({
          id: idx,
          category: rec[0],
          filed: rec[2],
          reviewed_and_actioned: rec[3],
          not_filed_inconclusive: rec[4],
          not_filed_setup: rec[5],
          not_filed_already: rec[6],
          error: rec[7],
        }));
        state.loadingTwo = false;
      })

      // Table Three
      .addCase(fetchTableThree.pending, (state) => {
        state.loadingThree = true;
      })
      .addCase(fetchTableThree.fulfilled, (state, action) => {
        state.tableThree = action.payload.map((rec, idx) => ({
          id: idx,
          output_date: rec[0],
          enabled_received: rec[1],
          filed_normal_count: rec[2],
          reviewed_out_of_range_count: rec[3],
          filed_abnormal_count: rec[4],
          reviewed_and_actioned_abnormal_count: rec[5],
          not_filed_already_filed_by_clinician_count: rec[6],
          not_filed_inconclusive_report_count: rec[7],
          not_filed_rules_not_setup_count: rec[8],
        }));
        state.loadingThree = false;
      })

      // Day By Category
      .addCase(fetchDayByCategory.pending, (state) => {
        state.loadingDayByCategory = true;
      })
      .addCase(fetchDayByCategory.fulfilled, (state, action) => {
        state.tableDayByCategory = action.payload.map((rec, idx) => ({
          id: idx,
          category: rec[0],
          filed: rec[2],
          reviewed_and_actioned: rec[3],
          not_filed_inconclusive: rec[4],
          not_filed_setup: rec[5],
          not_filed_already: rec[6],
          error: rec[7],
        }));
        state.loadingDayByCategory = false;
      })
      .addCase(fetchDayData.pending, (state) => {
        state.loadingDayData = true;
      })
      .addCase(fetchDayData.fulfilled, (state, action) => {
        state.tableDayData = action.payload.map((rec, idx) => ({
          id: idx,
          nhs_num: rec[0],
          emis_id: rec[1],
          lab_report_id: rec[2],
          category: rec[3],
          category_type: rec[4],
          status: rec[5],
          starttime: rec[6],
          notes: rec[7],
          report_has_review: Boolean(rec[8]),
          ordering_party: rec[9],
          usual_gp: rec[10],
        }));
        state.loadingDayData = false;
      })
      .addCase(fetchFiling.pending, (state) => {
        state.loadingLabReport = true;
      })
      .addCase(fetchFiling.fulfilled, (state, action) => {
        const report = action.payload;

        state.lab = {
          license_email: report.license_email || "",
          approved: report.approved ?? null,
          updated_at: report.updated_at || "",
          updated_by: report.updated_by || "",
          patient_data: report.patient_data || {},
          patient_profile: report.patient_profile || {},
          categories: Array.isArray(report.categories) ? report.categories : [],
          actions: Array.isArray(report.actions) ? report.actions : [],
          filing_error: report.filing_error || "",
        };
        state.loadingLabReport = false;
      })
      .addCase(fetchFiling.rejected, (state) => {
        state.loadingLabReport = false;
      });
  },
});

export const {
  resetDashboard,
  toggleLiverun,
  setSelectedDate,
  setSelectedDay,
  setLabReport,
} = dashFrameSlice.actions;

export default dashFrameSlice.reducer;
